<template>
  <div class="school-changes-page">
    <!-- 2022-2030 堆叠柱形图 -->
    <a-card class="chart-card" title="2022-2030年各类型学校总数堆叠柱形图">
      <div style="width: 100%; height: 450px">
        <!-- 关键：加 ref="chartRef" 以便拿到 chart 实例 -->
        <v-chart ref="chartRef" class="chart" :option="chartOption" autoresize />
      </div>
    </a-card>

    <!-- 下方两个表格 -->
    <div class="tables-container">
      <a-card class="table-card" title="撤并学校数 (2025-2030年)">
        <a-table
          :columns="closedSchoolColumns"
          :data="closedSchoolData"
          :pagination="false"
          :scroll="{ x: '100%', y: 400 }"
          size="small"
          bordered
        >
          <template #summary>
            <a-table-tr>
              <a-table-td :colspan="1">合计</a-table-td>
              <a-table-td v-for="year in displayYears" :key="`closed-total-${year}`" align="center">
                {{ getYearTotal(closedSchoolData, year) }}
              </a-table-td>
            </a-table-tr>
          </template>
        </a-table>
        <div class="table-note">
          <a-typography-text type="secondary">注：仅显示有撤并数据的年份</a-typography-text>
        </div>
      </a-card>

      <a-card class="table-card" title="新增学校数 (2025-2030年)">
        <a-table
          :columns="newSchoolColumns"
          :data="newSchoolData"
          :pagination="false"
          :scroll="{ x: '100%', y: 400 }"
          size="small"
          bordered
        >
          <template #summary>
            <a-table-tr>
              <a-table-td :colspan="1">合计</a-table-td>
              <a-table-td v-for="year in displayYears" :key="`new-total-${year}`" align="center">
                {{ getYearTotal(newSchoolData, year) }}
              </a-table-td>
            </a-table-tr>
          </template>
        </a-table>
        <div class="table-note">
          <a-typography-text type="secondary">注：仅显示有新增数据的年份</a-typography-text>
        </div>
      </a-card>
    </div>
  </div>
</template>

<script setup>
/* --------------------  下面是脚本  -------------------- */
import { ref, computed, onMounted, nextTick, onUnmounted } from 'vue';
import { use } from 'echarts/core';
import { BarChart } from 'echarts/charts';
import {
  TitleComponent,
  TooltipComponent,
  GridComponent,
  LegendComponent,
} from 'echarts/components';
import { CanvasRenderer } from 'echarts/renderers';
import VChart from 'vue-echarts';

use([TitleComponent, TooltipComponent, GridComponent, LegendComponent, BarChart, CanvasRenderer]);

/* --------------------  数据常量  -------------------- */
const allYears = ['2022', '2023', '2024', '2025', '2026', '2027', '2028', '2029', '2030'];
const displayYears = ['2025', '2026', '2027', '2028', '2029', '2030'];
const schoolTypes = [
  '幼儿园',
  '小学',
  '初中',
  '普通高中',
  '特殊教育学校',
  '专门学校',
  '中等职业学校',
];
const colorPalette = [
  '#5470c6',
  '#91cc75',
  '#fac858',
  '#ee6666',
  '#73c0de',
  '#3ba272',
  '#fc8452',
  '#9a60b4',
];

/* --------------------  读取 Excel 数据  -------------------- */
const getSchoolDataFromExcel = () => {
  const schoolTotals = {
    2022: {
      幼儿园: 508,
      小学: 461,
      初中: 85,
      普通高中: 22,
      特殊教育学校: 2,
      专门学校: 1,
      中等职业学校: 13,
    },
    2023: {
      幼儿园: 515,
      小学: 425,
      初中: 85,
      普通高中: 22,
      特殊教育学校: 2,
      专门学校: 1,
      中等职业学校: 13,
    },
    2024: {
      幼儿园: 492,
      小学: 408,
      初中: 85,
      普通高中: 22,
      特殊教育学校: 3,
      专门学校: 2,
      中等职业学校: 13,
    },
    2025: {
      幼儿园: 479,
      小学: 399,
      初中: 85,
      普通高中: 22,
      特殊教育学校: 4,
      专门学校: 2,
      中等职业学校: 13,
    },
    2026: {
      幼儿园: 476,
      小学: 398,
      初中: 82,
      普通高中: 23,
      特殊教育学校: 4,
      专门学校: 2,
      中等职业学校: 12,
    },
    2027: {
      幼儿园: 465,
      小学: 372,
      初中: 82,
      普通高中: 23,
      特殊教育学校: 4,
      专门学校: 2,
      中等职业学校: 12,
    },
    2028: {
      幼儿园: 450,
      小学: 353,
      初中: 82,
      普通高中: 23,
      特殊教育学校: 4,
      专门学校: 2,
      中等职业学校: 12,
    },
    2029: {
      幼儿园: 444,
      小学: 334,
      初中: 80,
      普通高中: 23,
      特殊教育学校: 4,
      专门学校: 2,
      中等职业学校: 12,
    },
    2030: {
      幼儿园: 420,
      小学: 315,
      初中: 76,
      普通高中: 23,
      特殊教育学校: 4,
      专门学校: 2,
      中等职业学校: 12,
    },
  };
  const yearlyTotals = {};
  allYears.forEach((y) => {
    yearlyTotals[y] = Object.values(schoolTotals[y]).reduce((a, b) => a + b, 0);
  });

  const closedSchools = {
    2025: {
      幼儿园: 12,
      小学: 7,
      初中: 0,
      普通高中: 0,
      特殊教育学校: 0,
      专门学校: 0,
      中等职业学校: 0,
    },
    2026: {
      幼儿园: 8,
      小学: 2,
      初中: 4,
      普通高中: 0,
      特殊教育学校: 0,
      专门学校: 0,
      中等职业学校: 1,
    },
    2027: {
      幼儿园: 13,
      小学: 27,
      初中: 0,
      普通高中: 0,
      特殊教育学校: 0,
      专门学校: 0,
      中等职业学校: 0,
    },
    2028: {
      幼儿园: 17,
      小学: 19,
      初中: 0,
      普通高中: 0,
      特殊教育学校: 0,
      专门学校: 0,
      中等职业学校: 0,
    },
    2029: {
      幼儿园: 7,
      小学: 20,
      初中: 2,
      普通高中: 0,
      特殊教育学校: 0,
      专门学校: 0,
      中等职业学校: 0,
    },
    2030: {
      幼儿园: 24,
      小学: 20,
      初中: 5,
      普通高中: 0,
      特殊教育学校: 0,
      专门学校: 0,
      中等职业学校: 0,
    },
  };
  const newSchools = {
    2025: {
      幼儿园: 0,
      小学: 0,
      初中: 0,
      普通高中: 0,
      特殊教育学校: 0,
      专门学校: 0,
      中等职业学校: 0,
    },
    2026: {
      幼儿园: 5,
      小学: 1,
      初中: 1,
      普通高中: 1,
      特殊教育学校: 0,
      专门学校: 0,
      中等职业学校: 0,
    },
    2027: {
      幼儿园: 2,
      小学: 1,
      初中: 0,
      普通高中: 0,
      特殊教育学校: 0,
      专门学校: 0,
      中等职业学校: 0,
    },
    2028: {
      幼儿园: 2,
      小学: 0,
      初中: 0,
      普通高中: 0,
      特殊教育学校: 0,
      专门学校: 0,
      中等职业学校: 0,
    },
    2029: {
      幼儿园: 1,
      小学: 1,
      初中: 0,
      普通高中: 0,
      特殊教育学校: 0,
      专门学校: 0,
      中等职业学校: 0,
    },
    2030: {
      幼儿园: 0,
      小学: 1,
      初中: 1,
      普通高中: 0,
      特殊教育学校: 0,
      专门学校: 0,
      中等职业学校: 0,
    },
  };
  return { schoolTotals, yearlyTotals, closedSchools, newSchools };
};

/* --------------------  表格列 / 数据  -------------------- */
const closedSchoolColumns = computed(() => {
  const cols = [
    { title: '学校类型', dataIndex: 'schoolType', key: 'schoolType', width: 120, fixed: 'left' },
  ];
  displayYears.forEach((y) =>
    cols.push({
      title: y,
      dataIndex: `year${y}`,
      key: `year${y}`,
      width: 80,
      align: 'center',
      render: ({ record }) => (record[`year${y}`] > 0 ? record[`year${y}`] : '-'),
    }),
  );
  return cols;
});
const newSchoolColumns = computed(() => JSON.parse(JSON.stringify(closedSchoolColumns.value)));

const closedSchoolData = ref([]);
const newSchoolData = ref([]);

const getYearTotal = (data, year) => {
  const k = `year${year}`;
  return data.reduce((t, r) => t + (r[k] || 0), 0);
};

/* --------------------  图表 option  -------------------- */
const chartOption = ref({});

/* --------------------  自动逐年轮播逻辑  -------------------- */
const chartRef = ref(null);
let timer = null;

const startYearLoop = (chartInst) => {
  let idx = 0;
  const next = (_) => {
    chartInst.dispatchAction({ type: 'hideTip' });
    chartInst.dispatchAction({ type: 'showTip', seriesIndex: 0, dataIndex: idx });
    idx = (idx + 1) % allYears.length;
  };
  next();
  timer = setInterval(next, 1500);
};
const stopYearLoop = () => {
  if (timer) {
    clearInterval(timer);
    timer = null;
  }
};
onUnmounted(stopYearLoop);

/* --------------------  初始化  -------------------- */
const initData = () => {
  const { schoolTotals, yearlyTotals, closedSchools, newSchools } = getSchoolDataFromExcel();

  // 表格数据
  closedSchoolData.value = schoolTypes.map((t, i) => {
    const r = { key: String(i + 1), schoolType: t };
    displayYears.forEach((y) => (r[`year${y}`] = closedSchools[y][t] || 0));
    return r;
  });
  newSchoolData.value = schoolTypes.map((t, i) => {
    const r = { key: String(i + 1), schoolType: t };
    displayYears.forEach((y) => (r[`year${y}`] = newSchools[y][t] || 0));
    return r;
  });

  // 图表系列
  const seriesData = schoolTypes.map((t, i) => ({
    name: t,
    type: 'bar',
    stack: 'total',
    emphasis: { focus: 'series' },
    itemStyle: { color: colorPalette[i % colorPalette.length] },
    data: allYears.map((y) => schoolTotals[y][t] || 0),
  }));
  // 总和标签系列（透明柱）
  const totalSeries = {
    name: '总数',
    type: 'bar',
    stack: 'total',
    itemStyle: { color: 'transparent' },
    label: {
      show: true,
      position: 'top',
      fontSize: 14,
      fontWeight: 'bold',
      color: '#333',
      formatter: ({ dataIndex }) => yearlyTotals[allYears[dataIndex]],
    },
    data: allYears.map(() => 0),
    silent: true,
    z: 1,
  };

  chartOption.value = {
    title: {
      text: '玉溪市2022-2030年各类型学校总数',
      left: 'center',
      textStyle: { fontSize: 26, fontWeight: 'bold' },
      top: 0,
    },
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'shadow' },
      position: function (point) {
        // 让 tooltip 整体下移 10 像素，避免挡住标题
        return [point[0], point[1] + 10];
      },
      formatter: (params) => {
        let html = `<div style="font-weight:bold;margin-bottom:5px;">${params[0].name}</div>`;
        let tot = 0;
        const valid = params.filter((p) => p.seriesName !== '总数');
        valid.forEach((p) => {
          tot += p.value;
          html += `
          <div style="display:flex;align-items:center;margin:2px 0;">
            <div style="width:10px;height:10px;background:${p.color};border-radius:2px;margin-right:5px;"></div>
            <div>${p.seriesName}: ${p.value}所</div>
          </div>`;
        });
        html += `<hr style="margin:5px 0;border-color:#eee;"><div style="font-weight:bold;">总计: ${tot}所</div>`;
        return html;
      },
    },
    legend: {
      data: schoolTypes,
      top: 30,
      itemWidth: 12,
      itemHeight: 12,
      type: 'scroll',
      orient: 'horizontal',
    },
    grid: {
      left: '3%',
      right: '4%',
      bottom: '12%',
      top: '15%',
      containLabel: true,
    },
    xAxis: {
      type: 'category',
      data: allYears,
    },
    yAxis: {
      type: 'value',
      name: '学校数量（所）',
    },
    series: [...seriesData, totalSeries],
  };

  // 重要：等 DOM 渲染完再启动轮播
  nextTick(() => {
    const ci = chartRef.value?.chart;
    if (ci) startYearLoop(ci);
  });
};

onMounted(initData);
</script>

<style scoped>
/* --------------------  样式保持你原来不变  -------------------- */
.school-changes-page {
  padding: 20px;
  background: #f5f5f5;
  min-height: 100vh;
}
.chart-card {
  margin-bottom: 24px;
}
.chart {
  width: 100%;
  height: 100%;
}
.tables-container {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
}
.table-card {
  height: 100%;
  overflow: hidden;
}
.table-card :deep(.arco-card-body) {
  padding: 0;
}
.table-card :deep(.arco-table) {
  border-radius: 0;
}
.table-card :deep(.arco-table-th) {
  background: #fafafa;
  font-weight: 600;
}
.table-card :deep(.arco-table-td) {
  border-right: 1px solid var(--color-border);
}
.table-card :deep(.arco-table-summary) {
  background: #fafafa;
  font-weight: 600;
}
.table-note {
  padding: 12px;
  border-top: 1px solid var(--color-border);
  background: #fafafa;
  font-size: 12px;
}

@media (max-width: 1200px) {
  .tables-container {
    grid-template-columns: 1fr;
  }
}
@media (max-width: 768px) {
  .school-changes-page {
    padding: 12px;
  }
  .chart-card {
    margin-bottom: 16px;
  }
  .tables-container {
    gap: 16px;
  }
}
</style>
